import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { formatTagLabel } from '../lib/formatTag';
import { Badge, Button, Select } from '../atoms';
import type { SelectOption } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable, Pagination, DocumentUploadModal, ListToolbar, useToast } from '../organisms';
import { useServerList } from '../lib/useServerList';
import { useEntityPermission } from '../lib/usePermissions';
import { formatDate } from '../lib/orgTimezone';

/**
 * DocumentsList — /v2/documents
 *
 * Bespoke (not GenericListByConfig) because document creation needs the
 * Upload modal flow, not the generic schema-driven CreateEntityModal.
 *
 * GRANULAR server-side search / sort / paginate: the debounced search box, the
 * document-type filter, the sortable column headers, and the 20-per-page pager all
 * drive the enriched documents search endpoint
 * (`GET /api/documents/search?q&document_type&sort&dir&page&limit`). That endpoint
 * JOINs clients / properties / parcels so a single `q` matches across the
 * document's name / filename / document_type AND its associated
 * client / property / parcel name (+ parcel account number) AND its year — reach
 * the generic-CRUD `GET /api/documents` list cannot provide (it LIKE-matches only
 * the documents table's own columns and can't search the integer year). Org scope
 * is enforced server-side; there is NO client-side filtering or slicing — the
 * server returns exactly the page shown. Mirrors the client detail "All documents"
 * toolbar (search + type filter + 20/page), server-side because the GLOBAL list
 * spans every client and must page rather than fetch-all.
 *
 * Phase 11 Block 11 PR 4 (2026-05-12); rewired for server-side list search
 * (doc-search fix — the Documents list had no search box); granular related-entity
 * + year search + type filter added over the enriched search endpoint.
 */

const PAGE_SIZE = 20;

interface Document {
  id: string;
  name: string;
  document_type: string | null;
  filename: string;
  content_type: string | null;
  file_size_bytes: number | null;
  year: number;
  status: string | null;
  property_id: string | null;
  parcel_id: string | null;
  client_id: string | null;
  created_at: string;
  // Enriched by GET /api/documents/search (LEFT JOINs) so the "Associated" column
  // can show — and the granular search can match — the related entity's name.
  client_name?: string | null;
  property_name?: string | null;
  parcel_name?: string | null;
  parcel_account_number?: string | null;
  // Assignee (enriched by the search endpoint's users join, #33) — powers the
  // "Assigned to" column's per-row assign control / read-only email.
  assigned_to_user_id?: string | null;
  assigned_to_email?: string | null;
}

interface ViewStatus {
  document_id: string;
  view_count: number;
  downloaded: boolean;
  last_viewed_at: string | null;
  last_viewer: string | null;
}

interface StaffUser {
  id: string;
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
}

interface AssignPayload {
  id: string;
  assigned_to_user_id: string | null;
}

/** Portal/client tiers can never be a staff reviewer — kept out of the picker. */
const NON_STAFF_ROLES = new Set(['client', 'consultant']);

function staffLabel(u: StaffUser): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name || u.email;
}

export function DocumentsList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  // Exact document-type filter (the toolbar Select), forwarded server-side.
  const [docType, setDocType] = useState('');
  // Upload POSTs /api/documents/upload → gate on document.can_add (download stays open).
  const canAdd = useEntityPermission('document', 'can_add');
  // Assigning a reviewer PATCHes /api/documents/:id/assign → gate on document.can_edit
  // (staff/agent hold it). View-only roles see the current assignee read-only instead
  // of the picker, matching the Document Review Queue.
  const canEdit = useEntityPermission('document', 'can_edit');

  // Server-side list: the debounced search box, the type filter, the sortable
  // headers, and the 20/page pager all drive GET /api/documents/search
  // (granular enriched envelope: q matches across the doc + its related
  // client/property/parcel name + year; org-scoped server-side).
  const list = useServerList<Document>({
    resource: 'documents/search',
    queryKey: ['documents-list', 'search'],
    defaultPageSize: PAGE_SIZE,
    defaultSort: { id: 'created_at', desc: true },
    filters: { document_type: docType },
  });
  const { query } = list;

  // Document-type options for the filter Select — the org's configured types
  // (same source the upload modal uses), so the filter offers every real type.
  const docTypesQuery = useQuery<string[] | { values?: string[] }>({
    queryKey: ['org-settings', 'document-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/document-types', signal),
  });
  const docTypeOptions = useMemo<SelectOption[]>(() => {
    const data = docTypesQuery.data;
    const values = Array.isArray(data) ? data : data?.values ?? [];
    return values.map((t) => ({ value: t, label: t }));
  }, [docTypesQuery.data]);

  // Staff readback (08.2): per-shared-document client viewed-status. One row per
  // document the firm has shared; view_count > 0 means the client opened it. Keyed
  // into a map for O(1) per-row lookup in the "Client viewed" column below.
  const viewStatusQuery = useQuery<ViewStatus[]>({
    queryKey: ['documents-view-status'],
    queryFn: ({ signal }) => api.get<ViewStatus[]>('/api/documents/view-status', signal),
  });
  const viewStatus = useMemo(
    () => new Map((viewStatusQuery.data ?? []).map((v) => [v.document_id, v])),
    [viewStatusQuery.data],
  );

  // Staff users for the assignee picker (portal/client tiers excluded) — the same
  // source + filter the Document Review Queue uses. Only fetched when the caller can
  // actually assign; a view-only role just reads the joined assigned_to_email.
  const staffQuery = useQuery<StaffUser[]>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get<StaffUser[]>('/api/users-list', signal),
    staleTime: 5 * 60 * 1000,
    enabled: canEdit,
  });
  const staff = useMemo(
    () => (staffQuery.data ?? []).filter((u) => !NON_STAFF_ROLES.has(u.role)),
    [staffQuery.data],
  );

  // Assign / reassign / unassign a reviewer. On success, refresh the documents list
  // so the row's assignee reflects the change (invalidates the useServerList query,
  // whose key is prefixed ['documents-list', 'search', …]).
  const assignMutation = useMutation<unknown, Error, AssignPayload>({
    mutationFn: ({ id, assigned_to_user_id }) =>
      api.patch(`/api/documents/${id}/assign`, { assigned_to_user_id }),
    onSuccess: (_data, vars) => {
      toast({ variant: 'success', message: vars.assigned_to_user_id ? 'Document assigned.' : 'Document unassigned.' });
      queryClient.invalidateQueries({ queryKey: ['documents-list'] });
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const columns = useMemo<ColumnDef<Document>[]>(() => [
    { accessorKey: 'name', header: 'Name' },
    {
      accessorKey: 'document_type',
      header: 'Type',
      cell: ({ getValue }) => {
        const v = getValue() as string | null;
        return v ? <Badge variant="neutral">{formatTagLabel(v)}</Badge> : '—';
      },
    },
    {
      accessorKey: 'filename',
      header: 'File',
      cell: ({ getValue }) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>
          {(getValue() as string) || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'file_size_bytes',
      header: 'Size',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        if (v == null) return '—';
        if (v < 1024) return `${v}B`;
        if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)}KB`;
        return `${(v / 1024 / 1024).toFixed(1)}MB`;
      },
    },
    { accessorKey: 'year', header: 'Year' },
    {
      id: 'associated',
      header: 'Associated',
      // Display-only (no real column to ORDER BY) — the enriched search endpoint
      // supplies the joined names, and this surfaces WHY a related-entity search
      // matched. Parcel is the most specific link, then property, then client.
      enableSorting: false,
      cell: ({ row }) => {
        const d = row.original;
        const label = d.parcel_name || d.parcel_account_number || d.property_name || d.client_name;
        return label ? <span>{label}</span> : <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => <Badge variant="neutral">{(getValue() as string) || 'Received'}</Badge>,
    },
    {
      id: 'assignee',
      accessorKey: 'assigned_to_user_id',
      header: 'Assigned to',
      // Interactive per-row assign control mirroring the Document Review Queue.
      // The backend can't ORDER BY the assignee (not in SEARCH_SORT_COLUMNS), so
      // this is display-only — never a sort header. stopPropagation keeps changing
      // the assignee from triggering the row-click to /documents/:id. View-only
      // roles (no document.can_edit) see the current assignee read-only.
      enableSorting: false,
      cell: ({ row }) => {
        const doc = row.original;
        if (!canEdit) {
          return doc.assigned_to_email
            ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{doc.assigned_to_email}</span>
            : <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
        }
        return (
          <div onClick={(e) => e.stopPropagation()} style={{ minWidth: 160 }}>
            <Select
              aria-label={`Assign ${doc.name}`}
              value={doc.assigned_to_user_id ?? ''}
              disabled={assignMutation.isPending}
              onChange={(e) => assignMutation.mutate({ id: doc.id, assigned_to_user_id: e.target.value || null })}
              options={[
                { value: '', label: 'Unassigned' },
                ...staff.map((u) => ({ value: u.id, label: staffLabel(u) })),
              ]}
            />
          </div>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Uploaded',
      cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }),
    },
    {
      id: 'client_viewed',
      header: 'Client viewed',
      cell: ({ row }) => {
        const vs = viewStatus.get(row.original.id);
        // Not in the map → the document isn't shared to the client; nothing to report.
        if (!vs) return <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
        if (vs.view_count > 0) {
          const when = vs.last_viewed_at ? formatDate(vs.last_viewed_at, { dateOnly: true }) : null;
          return (
            <Badge variant="success">
              {vs.downloaded ? 'Downloaded' : 'Viewed'}{when ? ` ${when}` : ''}
            </Badge>
          );
        }
        return <Badge variant="warning">Not viewed</Badge>;
      },
    },
    {
      id: 'download',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            downloadDocument(row.original.id, (m) => toast({ variant: 'error', message: m }));
          }}
        >
          Download
        </Button>
      ),
    },
  ], [viewStatus, toast, canEdit, staff, assignMutation]);

  const { rows: docs, total } = list;

  return (
    <>
      <PageHeader
        title="Documents"
        subtitle={query.isLoading ? 'Loading…' : `${total.toLocaleString()} document${total === 1 ? '' : 's'}`}
        actions={
          canAdd ? (
            <Button variant="primary" size="md" onClick={() => setUploadOpen(true)}>
              + Upload
            </Button>
          ) : null
        }
      />

      <DocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />

      <ListToolbar
        searchValue={list.searchInput}
        onSearchChange={list.setSearchInput}
        searchPlaceholder="Search by name, file, type, client / property / parcel, year…"
        filters={[
          {
            key: 'document_type',
            value: docType,
            onChange: setDocType,
            placeholder: 'All types',
            options: docTypeOptions,
            widthPx: 200,
          },
        ]}
      />

      {query.error && (
        <div style={{
          padding: 'var(--sp-4)',
          background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
          marginBottom: 'var(--sp-4)',
        }}>
          <strong>Failed to load documents:</strong> {(query.error as Error).message}
        </div>
      )}

      {!query.isLoading && total === 0 && !query.error && (
        (list.search !== '' || docType !== '') ? (
          <div style={{
            padding: 'var(--sp-7)',
            background: 'var(--color-card)',
            border: '1px dashed var(--color-border)',
            borderRadius: 'var(--r-lg)',
            color: 'var(--color-muted)',
            textAlign: 'center',
          }}>
            <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>No documents match your search.</p>
          </div>
        ) : (
          <div style={{
            padding: 'var(--sp-8)',
            background: 'var(--color-card)',
            border: '1px dashed var(--color-border)',
            borderRadius: 'var(--r-lg)',
            color: 'var(--color-muted)',
            textAlign: 'center',
          }}>
            <h2 style={{
              margin: '0 0 var(--sp-2)',
              fontSize: 'var(--fs-xl)',
              fontWeight: 'var(--fw-semibold)',
              color: 'var(--color-ink-3)',
            }}>No documents yet</h2>
            <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
              Click "+ Upload" to add a document (5MB max).
            </p>
          </div>
        )
      )}

      {(total > 0 || query.isFetching) && !query.error && (
        <>
          <DataTable
            data={docs}
            columns={columns}
            sorting={list.sorting}
            onSortingChange={list.setSorting}
            onRowClick={(row) => navigate(`/documents/${row.id}`)}
            emptyMessage={query.isFetching ? 'Loading…' : 'No documents'}
          />
          <Pagination
            pageIndex={list.pageIndex}
            pageCount={list.pageCount}
            pageSize={list.pageSize}
            totalRows={total}
            onPageChange={list.setPageIndex}
            onPageSizeChange={list.setPageSize}
          />
        </>
      )}
    </>
  );
}
